/* eslint react-hooks/exhaustive-deps: "warn" */
import { type KeyboardEvent, useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { Link } from 'libs/router/Link';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './InsuranceMonitoring.module.scss';

type Translate = (key: string) => string;

type Alert = {
  k: string;
  hi?: boolean;
  text: string;
  flag: string;
  source: string;
  time: string;
};

const getTopWatch = (t: Translate): string[] => [
  t('Filing and status changes, including strike-off and insolvency processes'),
  t('Directors appointed, resigned or disqualified'),
  t('Activity codes changed at confirmation or annual return'),
  t('Ownership and beneficial-ownership movements'),
  t('Charges registered or satisfied'),
  t('New accounts filed, with the figures that moved'),
];

const getTopFeed = (t: Translate): Alert[] => [
  {
    k: t('Insolvency'),
    hi: true,
    text: t('Administrators appointed at insured entity'),
    flag: '🇬🇧',
    source: 'Companies House · 24 Jul 2026',
    time: '4d',
  },
  {
    k: t('Activity'),
    hi: true,
    text: t('SIC code changed to hazardous waste treatment'),
    flag: '🇬🇧',
    source: 'Confirmation statement · 04 Feb 2026',
    time: '5d',
  },
  {
    k: t('Ownership'),
    text: t('Majority shareholding transferred to a new parent'),
    flag: '🇩🇪',
    source: 'Handelsregister · 21 Jul 2026',
    time: '6d',
  },
  {
    k: t('Officers'),
    text: t('Two directors resigned at a coverholder'),
    flag: '🇮🇪',
    source: 'CRO · B10 filed 20 Jul 2026',
    time: '7d',
  },
  {
    k: t('Financials'),
    text: t('Accounts filed, turnover up 41% on prior year'),
    flag: '🇫🇷',
    source: 'Infogreffe · FY2025 · 18 Jul 2026',
    time: '9d',
  },
  {
    k: t('Charges'),
    text: t('Second fixed charge registered over trading assets'),
    flag: '🇳🇱',
    source: 'KVK Handelsregister · 15 Jul 2026',
    time: '12d',
  },
];

type Population = {
  label: string;
  heading: string;
  tl: string;
  watch: string[];
  why: string;
  feedLabel: string;
  alerts: Alert[];
};

const getPopulations = (t: Translate): Population[] => [
  {
    label: t('Insureds'),
    heading: t('The insured moves inside the policy period.'),
    tl: t(
      'Twelve months sit between bind and renewal invitation. In that window an insured can change what it does, who owns it and whether it is solvent, and every one of those is a dated filing.',
    ),
    watch: [
      t('Activity code changed at confirmation or annual return'),
      t('Accounts filed, with the figures that moved against the rated base'),
      t('Charges registered, satisfied or crystallised'),
      t('Insolvency notices, strike-off and administration appointments'),
      t('Ownership movements that change the group aggregate'),
    ],
    why: t('Rating base, aggregation and mid-term terms, before the renewal invitation is drafted.'),
    feedLabel: t('Sample · insureds'),
    alerts: [
      {
        k: t('Insolvency'),
        hi: true,
        text: t('Administrators appointed at an insured entity'),
        flag: '🇬🇧',
        source: 'Companies House · 24 Jul 2026',
        time: '4d',
      },
      {
        k: t('Activity'),
        hi: true,
        text: t('SIC changed to hazardous waste treatment'),
        flag: '🇬🇧',
        source: 'Confirmation statement · 04 Feb 2026',
        time: '5d',
      },
      {
        k: t('Financials'),
        text: t('Accounts filed, turnover up 41% on prior year'),
        flag: '🇫🇷',
        source: 'Infogreffe · FY2025',
        time: '9d',
      },
      {
        k: t('Charges'),
        text: t('Second fixed charge over trading assets'),
        flag: '🇳🇱',
        source: 'KVK Handelsregister · 15 Jul 2026',
        time: '12d',
      },
    ],
  },
  {
    label: t('Coverholders and MGAs'),
    heading: t('A binder holder changes between audits.'),
    tl: t(
      'Delegated authority is approved on a file and reviewed on a cycle. Control of the entity holding the binder can change in the months between, without a single line of your own book changing.',
    ),
    watch: [
      t('Directors appointed, resigned or disqualified'),
      t('Shareholding and ultimate control transferred'),
      t('Registered status lapsing, dissolved or restored'),
      t('Regulatory identifiers changed or withdrawn'),
      t('Adverse filings against the entity or its owners'),
    ],
    why: t('Oversight evidence for delegated authority reviews, dated at the point the change was filed.'),
    feedLabel: t('Sample · coverholders and MGAs'),
    alerts: [
      {
        k: t('Control'),
        hi: true,
        text: t('Majority shareholding in a coverholder transferred'),
        flag: '🇩🇪',
        source: 'Handelsregister · 21 Jul 2026',
        time: '6d',
      },
      {
        k: t('Officers'),
        text: t('Two directors resigned at a binder holder'),
        flag: '🇮🇪',
        source: 'CRO · B10 filed 20 Jul 2026',
        time: '7d',
      },
      {
        k: t('Status'),
        text: t('Appointed representative entity marked dormant'),
        flag: '🇬🇧',
        source: 'Companies House · 16 Jul 2026',
        time: '11d',
      },
      {
        k: t('Ownership'),
        hi: true,
        text: t('New beneficial owner recorded above an MGA'),
        flag: '🇳🇱',
        source: 'UBO register · 14 Jul 2026',
        time: '13d',
      },
    ],
  },
  {
    label: t('Claims counterparties'),
    heading: t('The payee is not the party you underwrote.'),
    tl: t(
      'Third-party claimants, repairers, salvors, adjusters and assignees sit outside the original screen entirely. Each one is a company you are about to pay, assessed at the moment of payment rather than at bind.',
    ),
    watch: [
      t('Directors and addresses shared across claimant entities'),
      t('Incorporation and dissolution patterns around a loss date'),
      t('Group links between claimants, suppliers and repairers'),
      t('Designations and ownership tests applied to the payee'),
      t('The record as it stood on the date of loss, not today'),
    ],
    why: t('Whether a claim sits inside a network, and whether the payment can lawfully be made.'),
    feedLabel: t('Sample · claims counterparties'),
    alerts: [
      {
        k: t('Network'),
        hi: true,
        text: t('Three claimant entities share one registered address'),
        flag: '🇬🇧',
        source: 'Companies House · 23 Jul 2026',
        time: '5d',
      },
      {
        k: t('Officers'),
        text: t('Repairer and claimant share a common director'),
        flag: '🇬🇧',
        source: 'Appointments register',
        time: '8d',
      },
      {
        k: t('Screening'),
        hi: true,
        text: t('Payee resolves to an owner above a designation threshold'),
        flag: '🇨🇾',
        source: 'Registrar of Companies',
        time: '10d',
      },
      {
        k: t('Timing'),
        text: t('Claimant incorporated eleven days before the loss'),
        flag: '🇮🇪',
        source: 'CRO · incorporation record',
        time: '14d',
      },
    ],
  },
  {
    label: t('Credit and surety buyers'),
    heading: t('A limit set in March is a guess by September.'),
    tl: t(
      "Trade credit and surety carry the buyer's financial position, not the policyholder's. Filed accounts, charges and insolvency events move on the registry's calendar, and the limit should move with them.",
    ),
    watch: [
      t('New accounts filed, with the movement against the prior period'),
      t('Charges registered over trading assets'),
      t('Late filing, overdue accounts and compulsory strike-off notices'),
      t("Insolvency processes entered anywhere in the buyer's group"),
      t('Parent changes that alter who ultimately stands behind the debt'),
    ],
    why: t('The limit you hold on a buyer, and how quickly you can defend pulling it.'),
    feedLabel: t('Sample · credit and surety buyers'),
    alerts: [
      {
        k: t('Filing'),
        hi: true,
        text: t('Accounts overdue, compulsory strike-off proposed'),
        flag: '🇬🇧',
        source: 'Companies House · 22 Jul 2026',
        time: '6d',
      },
      {
        k: t('Group'),
        hi: true,
        text: t('Insolvency process opened at a sister company'),
        flag: '🇪🇸',
        source: 'Registro Mercantil · 19 Jul 2026',
        time: '9d',
      },
      {
        k: t('Charges'),
        text: t('Floating charge registered over inventory'),
        flag: '🇮🇹',
        source: 'Registro Imprese · 17 Jul 2026',
        time: '11d',
      },
      {
        k: t('Ownership'),
        text: t('Buyer sold out of its parent group'),
        flag: '🇫🇷',
        source: 'RCS · 12 Jul 2026',
        time: '16d',
      },
    ],
  },
  {
    label: t('Reinsurance counterparties'),
    heading: t('Cessions concentrate faster than the treaty renews.'),
    tl: t(
      'Panel members, fronting partners and captives are legal entities in their own right, filing in their own jurisdictions. Group changes among them redraw your counterparty concentration without any policy moving.',
    ),
    watch: [
      t('Ownership changes across panel entities and their parents'),
      t('Group structures that quietly merge two counterparties into one'),
      t('Registered status, redomiciliation and entity migration'),
      t('Filed financial statements for entities that publish them'),
      t('Sanctions exposure resolved across the whole panel'),
    ],
    why: t('True counterparty concentration across the panel, before the treaty is placed again.'),
    feedLabel: t('Sample · reinsurance counterparties'),
    alerts: [
      {
        k: t('Concentration'),
        hi: true,
        text: t('Two panel members resolve to one ultimate parent'),
        flag: '🇱🇺',
        source: 'RCS · shareholding filing',
        time: '7d',
      },
      {
        k: t('Structure'),
        text: t('Fronting partner moved under a new holding company'),
        flag: '🇮🇪',
        source: 'CRO · 18 Jul 2026',
        time: '10d',
      },
      {
        k: t('Status'),
        text: t('Captive redomiciled to another jurisdiction'),
        flag: '🇨🇾',
        source: 'Registrar of Companies',
        time: '15d',
      },
      {
        k: t('Financials'),
        text: t('Filed statements published for a panel entity'),
        flag: '🇩🇪',
        source: 'Handelsregister · FY2025',
        time: '18d',
      },
    ],
  },
];

const Feed = ({ label, alerts, gate = true }: { label: string; alerts: Alert[]; gate?: boolean }) => {
  const [on, setOn] = useState(false);

  useEffect(() => {
    if (!gate) {
      return undefined;
    }

    const id = setTimeout(() => setOn(true), 30);

    return () => clearTimeout(id);
  }, [gate]);

  return (
    <div className={styles.feed}>
      <div className={styles.feedHead}>
        <span className={styles.dot} />
        {label}
      </div>
      {alerts.map((alert, i) => (
        <div
          className={cn(styles.al, on && styles.on)}
          style={{ transitionDelay: `${90 + i * 100}ms` }}
          key={alert.text}
        >
          <span className={cn(styles.k, alert.hi && styles.kHi)}>{alert.k}</span>
          <div>
            <b>{alert.text}</b>
            <span className={styles.sr}>
              <span className={styles.flag}>{alert.flag}</span> {alert.source}
            </span>
          </div>
          <span className={styles.t}>{alert.time}</span>
        </div>
      ))}
    </div>
  );
};

export const InsuranceMonitoring = () => {
  const { t } = useInt();
  const topFeedRef = useRef<HTMLDivElement>(null);
  const topFeedRevealed = useIntersection(topFeedRef, { threshold: 0.16 });
  const [activeTab, setActiveTab] = useState(0);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const topWatch = getTopWatch(t);
  const topFeed = getTopFeed(t);
  const populations = getPopulations(t);

  useEffect(() => {
    tabRefs.current = tabRefs.current.slice(0, populations.length);
  }, [populations.length]);

  const onTabKeyDown = (e: KeyboardEvent, i: number) => {
    let next: number | null = null;
    if (e.key === 'ArrowRight') {
      next = (i + 1) % populations.length;
    } else if (e.key === 'ArrowLeft') {
      next = (i - 1 + populations.length) % populations.length;
    }

    if (next === null) {
      return;
    }

    e.preventDefault();
    setActiveTab(next);
    tabRefs.current[next]?.focus();
  };

  const population = populations[activeTab];

  return (
    <Section className={styles.root}>
      <TitleBlock
        align="left"
        title={t('The policy period is twelve months. The register moves every week.')}
        className={styles.splitHead}
      />

      <div className={styles.split}>
        <div className={styles.watchCol}>
          <div className={styles.watchList}>
            {topWatch.map((text) => (
              <div className={styles.watchItem} key={text}>
                <i>→</i>
                <span>{text}</span>
              </div>
            ))}
          </div>
          <Link className={styles.tlink} route="/continuous-monitoring">
            {t('See how portfolio monitoring works →')}
          </Link>
        </div>

        <div ref={topFeedRef}>
          <Feed label={t('Book watch · sample stream · last 7 days')} alerts={topFeed} gate={topFeedRevealed} />
        </div>
      </div>

      <div className={styles.perpetual}>
        <div className={styles.perpetualHead}>
          <TitleBlock
            title={t('Every counterparty you carry, watched between reviews.')}
            description={t(
              'A renewal review is a snapshot on a date you chose. Registries publish on dates they choose. Perpetual monitoring closes the distance for every population on your book, not only the insured.',
            )}
          />
        </div>

        <div className={styles.tabs} role="tablist" aria-label={t('Monitored populations')}>
          {populations.map((pop, i) => (
            <button
              key={pop.label}
              ref={(el) => {
                tabRefs.current[i] = el;
              }}
              type="button"
              role="tab"
              aria-selected={activeTab === i}
              tabIndex={activeTab === i ? 0 : -1}
              className={cn(styles.tab, activeTab === i && styles.tabOn)}
              onClick={() => setActiveTab(i)}
              onKeyDown={(e) => onTabKeyDown(e, i)}
            >
              {pop.label}
            </button>
          ))}
        </div>

        <div key={activeTab}>
          <h3 className={styles.panelTitle}>{population.heading}</h3>
          <p className={cn(styles.panelText, styles.panelIntroText)}>{population.tl}</p>

          <div className={styles.panel}>
            <div className={styles.watchCol}>
              <div className={styles.watchList}>
                {population.watch.map((text) => (
                  <div className={styles.watchItem} key={text}>
                    <i>→</i>
                    <span>{text}</span>
                  </div>
                ))}
              </div>
              <p className={styles.why}>{population.why}</p>
            </div>

            <Feed label={population.feedLabel} alerts={population.alerts} />
          </div>
        </div>

        <div className={styles.cyc}>
          <div className={styles.cycCard}>
            <div className={styles.cycHead}>
              <span className={cn(styles.tag, styles.tagMut)}>{t('Periodic review')}</span>
              <span className={styles.gap}>365d</span>
            </div>
            <h3 className={styles.cycTitle}>{t('A snapshot on a date you chose.')}</h3>
            <p className={styles.cycText}>
              {t(
                'The file is refreshed at renewal, or at the next scheduled audit. Everything filed in between is discovered afterwards, usually because something went wrong.',
              )}
            </p>
          </div>
          <div className={cn(styles.cycCard, styles.cycAccent)}>
            <div className={styles.cycHead}>
              <span className={styles.tag}>{t('Perpetual monitoring')}</span>
              <span className={cn(styles.gap, styles.gapAccent)}>1d</span>
            </div>
            <h3 className={styles.cycTitle}>{t('A record that moves when the registry moves.')}</h3>
            <p className={styles.cycText}>
              {t(
                'Changes arrive on a daily-synced cadence carrying the filing behind them, routed to the desk that owns the account, with the date the change was made rather than the date you found it.',
              )}
            </p>
          </div>
        </div>
      </div>
    </Section>
  );
};
